import React, { useEffect, useState } from "react";
import SplitPane from "react-split-pane";
import { useParams } from "react-router";
import { FaEdit } from "react-icons/fa";
import { CiSearch } from "react-icons/ci";
import { FaTrash } from "react-icons/fa6";
import { Form, Table } from "react-bootstrap";
import { cdiscManagementService } from "../../../APIServices/CDISC/cdiscManagementService";
import {
  NoDataFoundTable,
  PageLoader,
} from "../../../Components/NoDataFoundTable/NoDataFoundTable";
import Swal from "sweetalert2";
import Moment from "react-moment";
import Pagination from "../../Pagination";
import UseToggle from "../../../Hooks/UseToggle";
import PullCMapMetadata from "./PullCMapMetadata";
import AddNewCMapMetadata from "./AddNewCMapMetadata";
import UpdateCMapMetadata from "./UpdateCMapMetadata";
import DeletedCMapMetadata from "./DeletedCMapMetadata";
import TableProperties from "./Properties/TableProperties";
import CMapMetadataActivities from "./Properties/CMapMetadataActivities";
import { studyOnboardingService } from "../../../APIServices/studyOnboarding";
import { studyOtherSettingsServices } from "../../../APIServices/studyOtherSettingsServices";
import ImportCMapData from "./Properties/ImportCMapData";

const CMapManagement = ({ onMouseDown, tableRef }) => {
  const toggles = UseToggle();
  const { studyId } = useParams();
  const [loader, setLoader] = useState(false);
  const [searchData, setSearchData] = useState("");
  const [cMapMetadata, setCMapMetadata] = useState([]);
  const [cMapMetadataCount, setCMapMetadataCount] = useState(0);
  const [cMapData, setCMapData] = useState("");
  const [isFormOpen, setIsFormOpen] = useState("");
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [cMapId, setCMapId] = useState("");
  const [activeTab, setActiveTab] = useState("activities");
  const [tableColumns, setTableColumns] = useState({});
  const StudyMode = JSON.parse(localStorage.getItem("StudyMode"));

  const formColumn = [
    {
      columnLabel: "CodeList Name",
      columnName: "CODELISTNAME",
      columnType: "text",
    },
    {
      columnLabel: "Source",
      columnName: "SOURCE",
      columnType: "text",
    },
    {
      columnLabel: "Target",
      columnName: "TARGET",
      columnType: "text",
    },
    {
      columnLabel: "Created Date | Time",
      columnName: "CREATED",
      columnType: "date & time",
    },
    {
      columnLabel: "Updated Date | Time",
      columnName: "UPDATED",
      columnType: "date & time",
    },
    { columnLabel: "Edit", columnName: "EDIT", columnType: "action" },
    { columnLabel: "Delete", columnName: "DELETE", columnType: "action" },
  ];

  const [pageNumber, setPageNumber] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const startIndex = (pageNumber - 1) * itemsPerPage;
  const totalPage = Math.ceil(cMapMetadataCount / itemsPerPage);

  const getStudyCDISCCMap = async () => {
    setLoader(true);
    let data = await cdiscManagementService.getStudyCDISCCMap(
      "active",
      studyId,
      searchData,
      pageNumber,
      itemsPerPage
    );
    setCMapMetadata(data?.data || []);
    setCMapMetadataCount(data?.count || 0);
    setLoader(false);
  };

  const downloadCMapTemplate = async () => {
    let data = await cdiscManagementService.downloadCMapTemplate();
    const csvContent = (data?.data || []).join(",") + "\n";
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "CMap-Template.csv";
    link.click();
  };

  useEffect(() => {
    getStudyCDISCCMap();
  }, [studyId, searchData, pageNumber, itemsPerPage]);

  const updateCMapModal = (item) => {
    setCMapData(item);
    toggles.setToggle();
  };

  const [pageNumberDeleted, setPageNumberDeleted] = useState(1);
  const [itemsPerPageDeleted, setItemsPerPageDeleted] = useState(10);
  const [deletedCMapCount, setDeletedCMapCount] = useState(0);
  const [deletedCMap, setDeletedCMap] = useState([]);

  const getDeletedCMapList = async () => {
    let data = await cdiscManagementService.getStudyCDISCCMap(
      "deleted",
      studyId,
      "",
      pageNumberDeleted,
      itemsPerPageDeleted
    );
    setDeletedCMap(data?.data || []);
    setDeletedCMapCount(data?.count || 0);
  };

  const softDeleteRestoreStudyCDISCCMaps = async (id, type) => {
    Swal.fire({
      title: "Are you sure?",
      text: `You want to ${type} cmaps!`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#103C5E",
      cancelButtonColor: "#d33",
      confirmButtonText: `Yes, ${type} it!`,
    }).then(async (result) => {
      if (result.isConfirmed) {
        let userData = {};
        userData.id = id;
        userData.studyId = studyId;
        userData.type = type;
        let data =
          await cdiscManagementService.softDeleteRestoreStudyCDISCCMaps(
            userData
          );
        if (data?.statusCode === 200) {
          getStudyCDISCCMap();
          getDeletedCMapList();
          setCMapId("");
          if (type === "restore") {
            getDeletedCMapList(pageNumberDeleted, itemsPerPageDeleted);
            toggles.setToggle1();
          }
        }
      }
    });
  };

  const [formActivity, setFormActivity] = useState([]);
  const [formActivityCount, setFormActivityCount] = useState(0);
  const [activityLoader, setActivityLoader] = useState(false);
  const [buttonLoader, setButtonLoader] = useState(false);

  const getStudyCDISCAuditTrials = async (pageNumber, itemsPerPage) => {
    setActivityLoader(true);
    let data = await studyOnboardingService.getStudyCDISCAuditTrials(
      "CDISCCMapMetadata",
      studyId,
      cMapData?.cMapId,
      StudyMode,
      pageNumber,
      itemsPerPage
    );
    setFormActivity(data?.data || []);
    setFormActivityCount(data?.count || 0);
    setActivityLoader(false);
  };

  const getGlobalTablePropertiesData = async (e) => {
    let data = await studyOtherSettingsServices.getGlobalTablePropertiesData(
      "cdiscControlTerm"
    );
    setTableColumns(data?.data || {});
  };

  useEffect(() => {
    getGlobalTablePropertiesData();
  }, []);

  const updateTableProperties = async () => {
    Swal.fire({
      title: "Are you sure?",
      text: `You want to update table property!`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#103C5E",
      cancelButtonColor: "#d33",
      confirmButtonText: `Yes, Update it!`,
    }).then(async (result) => {
      if (result.isConfirmed) {
        setButtonLoader(true);
        let userData = {};
        userData.type = "cdiscControlTerm";
        userData.columns = tableColumns;
        let data = await studyOtherSettingsServices.updateTableProperties(
          userData
        );
        if (data?.statusCode === 200) {
          getStudyCDISCCMap();
          getGlobalTablePropertiesData();
        }
      }
      setButtonLoader(false);
    });
  };

  return (
    <>
      <div
        className="justify-content-between toolbar border-bottom hstack p-1"
        style={{ height: 36 }}
      >
        <div className="hstack gap-1">
          <i
            title={isFormOpen ? "Close Form" : "Add CMap Metadata"}
            onClick={() => {
              setIsFormOpen(!isFormOpen);
              setCMapId("");
            }}
            className={`fa-solid ${
              isFormOpen ? "fa-table-list" : "fa-plus-square CP"
            }`}
          />
          <div className="vr"></div>
          {cMapMetadataCount > 0 && (
            <div className="position-relative">
              <Form.Control
                type="Search"
                onChange={(e) => setSearchData(e.target.value)}
                value={searchData}
                placeholder="Search..."
                style={{ width: 200, padding: "5px 10px" }}
              ></Form.Control>
              <CiSearch
                className="CP position-absolute bg-white"
                title="Search"
                style={{ width: 15, top: 5, right: 7 }}
              />
            </div>
          )}
        </div>
        <div className="hstack gap-2">
          <button
            onClick={() => toggles.setToggle4()}
            className={`updateProfile`}
            style={{ fontSize: 12 }}
          >
            Import
          </button>
          <button
            onClick={() => toggles.setToggle3()}
            className={`updateProfile`}
            style={{ fontSize: 12 }}
          >
            Pull Metadata
          </button>
          {cMapMetadataCount > 0 && (
            <button
              onClick={() => toggles.setToggle2()}
              className={`updateProfile`}
              style={{ fontSize: 12 }}
            >
              Table Properties
            </button>
          )}
          {deletedCMapCount > 0 && (
            <button
              onClick={() => toggles.setToggle1()}
              className={`updateProfile`}
            >
              Deleted History
            </button>
          )}
        </div>
      </div>
      <SplitPane
        split="horizontal"
        className="position-relative"
        style={{ height: "calc(100vh - 182px)" }}
        minSize={cMapId ? "40%" : "100%"}
      >
        <SplitPane
          split="vertical"
          minSize={20}
          className="position-relative"
          defaultSize={isFormOpen ? "20%" : "100%"}
        >
          {isFormOpen && (
            <AddNewCMapMetadata
              studyId={studyId}
              getStudyCDISCCMap={getStudyCDISCCMap}
            />
          )}
          {loader ? (
            <PageLoader />
          ) : cMapMetadata?.length === 0 ? (
            <NoDataFoundTable MSG={"CMap metadata not found!"} />
          ) : (
            <>
              <Table
                ref={tableRef}
                className="m-0 custom-table custom-tables"
                hover
                striped
              >
                <thead className="position-sticky top-0">
                  <tr>
                    <th
                      onMouseDown={(e) => onMouseDown(e, 0)}
                      style={{ width: 45 }}
                      className="text-center"
                    >
                      Sr.
                    </th>
                    {!tableColumns["CODELISTNAME"] && (
                      <th onMouseDown={(e) => onMouseDown(e, 1)}>CodeList Name</th>
                    )}
                    {!tableColumns["SOURCE"] && (
                      <th onMouseDown={(e) => onMouseDown(e, 2)}>Source</th>
                    )}
                    {!tableColumns["TARGET"] && (
                      <th onMouseDown={(e) => onMouseDown(e, 3)}>Target</th>
                    )}
                    {!tableColumns["CREATED"] && (
                      <th
                        onMouseDown={(e) => onMouseDown(e, 4)}
                        style={{ width: 180 }}
                      >
                        Created Date | Time
                      </th>
                    )}
                    {!tableColumns["UPDATED"] && (
                      <th
                        onMouseDown={(e) => onMouseDown(e, 5)}
                        style={{ width: 180 }}
                      >
                        Updated Date | Time
                      </th>
                    )}
                    {!tableColumns["EDIT"] && (
                      <th
                        onMouseDown={(e) => onMouseDown(e, 6)}
                        style={{ width: 45 }}
                        className="text-center"
                      >
                        Edit
                      </th>
                    )}
                    {!tableColumns["DELETE"] && (
                      <th
                        onMouseDown={(e) => onMouseDown(e, 7)}
                        style={{ width: 55 }}
                        className="text-center"
                      >
                        Delete
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {cMapMetadata?.map((item, index) => (
                    <tr
                      key={index}
                      onClick={() => {
                        setIsFormOpen(false);
                        setCMapId(item?.cMapId);
                        setCMapData(item);
                      }}
                      className={`CP ${
                        cMapId === item?.cMapId ? "highlighted" : ""
                      }`}
                      title="View Details"
                    >
                      <td className="text-center">{startIndex + index + 1}</td>
                      {!tableColumns["CODELISTNAME"] && <td>{item.codeListName}</td>}
                      {!tableColumns["SOURCE"] && <td>{item.source}</td>}
                      {!tableColumns["TARGET"] && <td>{item.target}</td>}
                      {!tableColumns["CREATED"] && (
                        <td>
                          <Moment format="DD MMM YYYY | HH:mm" withTitle>
                            {item.createdAt}
                          </Moment>
                        </td>
                      )}
                      {!tableColumns["UPDATED"] && (
                        <td>
                          <Moment format="DD MMM YYYY | HH:mm" withTitle>
                            {item.updatedAt}
                          </Moment>
                        </td>
                      )}
                      {!tableColumns["EDIT"] && (
                        <td
                          className="text-center CP"
                          title="Update"
                          onClick={(e) => {
                            updateCMapModal(item);
                            e.stopPropagation();
                          }}
                        >
                          <FaEdit className="text-muted" />
                        </td>
                      )}
                      {!tableColumns["DELETE"] && (
                        <td
                          className="text-center CP"
                          title="Delete"
                          onClick={(e) => {
                            softDeleteRestoreStudyCDISCCMaps(
                              item._id,
                              "delete"
                            );
                            e.stopPropagation();
                          }}
                        >
                          <FaTrash className="text-danger" />
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </Table>
              <div className="position-sticky bottom-0 start-0">
                <Pagination
                  totalPage={totalPage}
                  pageNumber={pageNumber}
                  itemsPerPage={itemsPerPage}
                  totalItems={cMapMetadataCount}
                  setPageNumber={setPageNumber}
                  setItemsPerPage={setItemsPerPage}
                />
              </div>
            </>
          )}
        </SplitPane>
        {cMapId && (
          <div className={isFullScreen ? "full-screen-component" : ""}>
            <div className="hstack paginate fixed_tab_button justify-content-between pt-1 border-bottom bg-light">
              <div className="hstack me-1 gap-1">
                {["activities"].map((tab) => (
                  <button
                    key={tab}
                    className={`buttonForTabs px-2 ${
                      activeTab === tab ? "activee" : ""
                    }`}
                    onClick={() => setActiveTab(tab)}
                    title={tab.charAt(0).toUpperCase() + tab.slice(1)}
                  >
                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                  </button>
                ))}
              </div>
              <div className="d-flex gap-1">
                <div className="toolbar">
                  <i
                    className="fa-solid fa-angle-down"
                    onClick={() => setCMapId(false)}
                  ></i>
                </div>
                <div className="toolbar">
                  <i
                    className={`fa-solid me-2 ${
                      !isFullScreen ? "fa-maximize" : "fa-minimize"
                    }`}
                    title={!isFullScreen ? "Full Mode" : "Original Mode"}
                    onClick={() => setIsFullScreen(!isFullScreen)}
                  ></i>
                </div>
              </div>
            </div>
            {activeTab === "activities" && (
              <CMapMetadataActivities
                tableRef={tableRef}
                onMouseDown={onMouseDown}
                formActivity={formActivity}
                cMapData={cMapData}
                activityLoader={activityLoader}
                formActivityCount={formActivityCount}
                getStudyCDISCAuditTrials={getStudyCDISCAuditTrials}
              />
            )}
          </div>
        )}
      </SplitPane>
      <UpdateCMapMetadata
        studyId={studyId}
        cMapData={cMapData}
        getStudyCDISCCMap={getStudyCDISCCMap}
        Show={!toggles.toggle}
        Hide={toggles.setToggle}
        Title={"Update CMaps"}
      />
      <DeletedCMapMetadata
        Show={!toggles.toggle1}
        Hide={toggles.setToggle1}
        setCMapId={setCMapId}
        tableRef={tableRef}
        onMouseDown={onMouseDown}
        deletedCMap={deletedCMap}
        getDeletedCMapList={getDeletedCMapList}
        deletedCMapCount={deletedCMapCount}
        pageNumberDeleted={pageNumberDeleted}
        itemsPerPageDeleted={itemsPerPageDeleted}
        setPageNumberDeleted={setPageNumberDeleted}
        setItemsPerPageDeleted={setItemsPerPageDeleted}
        softDeleteRestoreStudyCDISCCMaps={softDeleteRestoreStudyCDISCCMaps}
        Title={"Deleted CMaps History"}
      />
      <TableProperties
        Show={!toggles.toggle2}
        Hide={toggles.setToggle2}
        Title={"Table Properties"}
        tableRef={tableRef}
        onMouseDown={onMouseDown}
        formColumn={formColumn}
        tableColumns={tableColumns}
        buttonLoader={buttonLoader}
        setTableColumns={setTableColumns}
        updateTableProperties={updateTableProperties}
      />

      <PullCMapMetadata
        Show={!toggles.toggle3}
        Hide={toggles.setToggle3}
        studyId={studyId}
        tableRef={tableRef}
        onMouseDown={onMouseDown}
        getStudyCDISCCMap={getStudyCDISCCMap}
        Title="Pull CMaps"
      />
      <ImportCMapData
        Show={!toggles.toggle4}
        Hide={toggles.setToggle4}
        Title={"Import CMap Data"}
        studyId={studyId}
        getStudyCDISCCMap={getStudyCDISCCMap}
        downloadCMapTemplate={downloadCMapTemplate}
      />
    </>
  );
};

export default CMapManagement;
